/**
 * SignalBoard — the plan graph as a railway signal board (leg `gui-signal-board`, data: R7).
 *
 * One TRACK (column) per build level, left to right; one STATION per (level, metadata type) showing its
 * type, count and one lamp per status present. Click a station to expand it into its items; click an
 * item to open the existing item view. The lowest track with unsettled items is highlighted, and the
 * edges leading into it carry a moving marker (CSS; off under `prefers-reduced-motion`).
 *
 * `SignalBoardView` is pure presentation of an already-fetched graph (static-render tested);
 * `SignalBoard` owns the fetching (`taskGraphController`, mounted from the task panel).
 *
 * Layout is arithmetic on ids and counts (`layoutBoard`) — no DOM measuring, deterministic order — so a
 * refetch that changes nothing re-renders the same picture.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { getTaskGraph } from "@/services/multi-agent/laneV2/api";
import {
  createTaskGraphController,
  GRAPH_POLL_MS,
  INITIAL_GRAPH_STATE,
  type TaskGraphController,
  type TaskGraphState,
} from "@/services/multi-agent/laneV2/taskGraphController";
import type { TaskGraph } from "@/services/multi-agent/protocol";
import type { TaskSnapshot } from "@/services/multi-agent/laneV2/types";
import {
  buildBoard,
  LAMP_LABEL,
  LAMP_ORDER,
  lampOf,
  layoutBoard,
  LAYOUT,
  RING_LABEL,
  ringOf,
  RING_ORDER,
  type LampKind,
  type RingKind,
} from "./boardModel";
import { isTerminalState } from "./views";

// ── lamps ────────────────────────────────────────────────────────────────────────────────────────

const LAMP_CLASS: Record<LampKind, string> = {
  // Shapes differ as well as colours: round = progress, square = failed, rounded-square = blocked/dropped.
  unlit: "rounded-full border border-muted-foreground/40 bg-transparent",
  dim: "rounded-full bg-muted-foreground/40",
  pulsing: "rounded-full bg-amber-400 sb-pulse",
  built: "rounded-full border-2 border-emerald-500 bg-transparent",
  validated: "rounded-full bg-emerald-500",
  failed: "rounded-none bg-red-500 sb-cross",
  blocked: "rounded-sm bg-muted-foreground/60 sb-barred",
  dropped: "rounded-sm border border-muted-foreground/40 bg-transparent opacity-50 sb-struck",
  unknown: "rounded-full border border-dashed border-muted-foreground/60 bg-transparent",
};
const RING_CLASS: Record<RingKind, string> = {
  not_validated: "ring-2 ring-amber-400",
  validation_failed: "ring-2 ring-red-500",
};

export function Lamp({ kind, ring, className }: { kind: LampKind; ring?: RingKind | null; className?: string }) {
  return (
    <span
      role="img"
      aria-label={`${LAMP_LABEL[kind]}${ring ? `, ${RING_LABEL[ring]}` : ""}`}
      data-lamp={kind}
      data-ring={ring ?? undefined}
      title={`${LAMP_LABEL[kind]}${ring ? ` · ${RING_LABEL[ring]}` : ""}`}
      className={cn("inline-block h-2.5 w-2.5 shrink-0", LAMP_CLASS[kind], ring && RING_CLASS[ring], className)}
    />
  );
}

const STYLES = `
@keyframes sb-flow { to { stroke-dashoffset: -16; } }
@keyframes sb-pulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
.sb-flow { stroke-dasharray: 4 4; animation: sb-flow .9s linear infinite; }
.sb-pulse { animation: sb-pulse 1.2s ease-in-out infinite; }
.sb-barred { background-image: repeating-linear-gradient(45deg, transparent 0 2px, rgba(0,0,0,.45) 2px 3px); }
.sb-cross { background-image: linear-gradient(45deg, transparent 44%, #fff 44% 56%, transparent 56%), linear-gradient(-45deg, transparent 44%, #fff 44% 56%, transparent 56%); }
.sb-struck { background-image: linear-gradient(135deg, transparent 45%, currentColor 45% 55%, transparent 55%); }
@media (prefers-reduced-motion: reduce) { .sb-flow, .sb-pulse { animation: none; } }
`;

// ── presentation ─────────────────────────────────────────────────────────────────────────────────

export interface SignalBoardViewProps {
  graph: Pick<TaskGraph, "nodes" | "edges">;
  /** Called with the Work item id of a clicked item that has a body. Absent = items are not clickable. */
  onOpenItem?: (itemId: string) => void;
  /** Node ids that has a body to open (`item_id` of work items with `has_body`). */
  openableIds?: ReadonlySet<string>;
  addedIds?: readonly string[];
  /** The build is running: only then is a track "active" and its edges animated. */
  buildActive?: boolean;
  /** Set when a build exists but this plan version has no status on any node (revised after the build). */
  notBuiltVersion?: number;
  /** Stations expanded on first render (tests; the user toggles afterwards). */
  initialExpanded?: readonly string[];
  className?: string;
}

export function SignalBoardView({ graph, onOpenItem, openableIds, addedIds, buildActive, notBuiltVersion, initialExpanded, className }: SignalBoardViewProps) {
  const board = useMemo(() => buildBoard(graph, { buildActive: !!buildActive }), [graph, buildActive]);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set(initialExpanded ?? []));
  const layout = useMemo(() => layoutBoard(board, expanded), [board, expanded]);
  const added = useMemo(() => new Set(addedIds ?? []), [addedIds]);
  const stationById = useMemo(() => new Map(board.stations.map((s) => [s.id, s])), [board]);
  const toggle = (id: string) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  if (board.stations.length === 0) return null;
  const present = new Map(board.lamps.map((l) => [l.lamp, l.count]));

  return (
    <section aria-label="Plan signal board" data-signal-board className={cn("rounded-md border border-border bg-card", className)}>
      <style>{STYLES}</style>
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3.5 py-2.5">
        <h3 className="text-[13px] font-semibold text-foreground">Signal board</h3>
        <span className="text-[11px] text-muted-foreground">
          {Object.keys(board.nodes).length} items · {board.tracks.length} track{board.tracks.length === 1 ? "" : "s"}
          {board.activeTrack !== null ? ` · building track ${board.activeTrack + 1}` : ""}
        </span>
      </header>

      {notBuiltVersion !== undefined && (
        <p role="status" data-not-built className="mx-3.5 mb-2 text-[11px] text-muted-foreground">
          Plan v{notBuiltVersion} is not built yet.
        </p>
      )}

      <div className="overflow-x-auto border-t border-border px-1 pb-2">
        <div className="relative" style={{ width: layout.width, height: layout.height }}>
          <svg width={layout.width} height={layout.height} className="pointer-events-none absolute inset-0" aria-hidden>
            {board.tracks.map((t) => (
              <rect
                key={t.index}
                x={LAYOUT.padX + t.index * LAYOUT.colWidth - 6}
                y={0}
                width={LAYOUT.stationWidth + 12}
                height={layout.height}
                rx={6}
                data-track={t.index}
                data-track-active={t.active ? "true" : undefined}
                className={t.active ? "fill-amber-400/10 stroke-amber-400/60" : "fill-muted/30 stroke-transparent"}
              />
            ))}
            {layout.edges.map((e) => {
              const mid = (e.x1 + e.x2) / 2;
              return (
                <path
                  key={e.key}
                  d={`M${e.x1},${e.y1} C${mid},${e.y1} ${mid},${e.y2} ${e.x2},${e.y2}`}
                  fill="none"
                  strokeWidth={Math.min(3, 1 + Math.log2(e.count))}
                  data-edge={e.key.replace("\u0000", ">")}
                  data-edge-active={e.active ? "true" : undefined}
                  className={cn(e.active ? "sb-flow stroke-amber-500" : "stroke-muted-foreground/40")}
                />
              );
            })}
          </svg>

          {layout.stations.map((p) => {
            const st = stationById.get(p.id)!;
            return (
              <div
                key={p.id}
                data-station={p.id}
                data-expanded={p.expanded ? "true" : undefined}
                className={cn(
                  "absolute rounded-md border bg-background text-[12px] shadow-sm",
                  board.tracks[st.track].active ? "border-amber-400/70" : "border-border",
                )}
                style={{ left: p.x, top: p.y, width: p.w, height: p.h }}
              >
                <button
                  type="button"
                  aria-expanded={p.expanded}
                  onClick={() => toggle(p.id)}
                  className="block w-full px-2 py-1.5 text-left hover:bg-muted/40"
                  style={{ height: LAYOUT.stationHeader }}
                >
                  <span className="flex items-baseline justify-between gap-1">
                    <span className="truncate font-mono font-medium text-foreground" title={st.metadataType}>
                      {st.metadataType}
                    </span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">{st.count}</span>
                  </span>
                  <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    {st.lamps.map((l) => (
                      <span key={l.lamp} className="inline-flex items-center gap-0.5 text-[10px] tabular-nums text-muted-foreground">
                        <Lamp kind={l.lamp} />
                        {l.count}
                      </span>
                    ))}
                    {st.rings.map((r) => (
                      <span key={r.ring} className="inline-flex items-center gap-0.5 text-[10px] tabular-nums text-muted-foreground">
                        <Lamp kind="unlit" ring={r.ring} />
                        {r.count}
                      </span>
                    ))}
                  </span>
                </button>
                {p.expanded && (
                  <ul className="overflow-y-auto border-t border-border/60" style={{ height: p.h - LAYOUT.stationHeader }}>
                    {p.rows.map((id) => {
                      const n = board.nodes[id];
                      const openable = !!onOpenItem && (openableIds ? openableIds.has(id) : true);
                      return (
                        <li key={id} data-item={id} data-item-new={added.has(id) ? "true" : undefined} style={{ height: LAYOUT.itemHeight }}>
                          <button
                            type="button"
                            disabled={!openable}
                            onClick={() => onOpenItem?.(id)}
                            title={n.full_name}
                            className="flex h-full w-full items-center gap-1.5 px-2 text-left font-mono text-[11px] hover:bg-muted/40 disabled:cursor-default disabled:hover:bg-transparent"
                          >
                            <Lamp kind={lampOf(n)} ring={ringOf(n)} />
                            <span className="truncate">{n.full_name}</span>
                            {added.has(id) && <span className="ml-auto text-[9px] uppercase text-amber-600">new</span>}
                          </button>
                        </li>
                      );
                    })}
                    {st.count > LAYOUT.maxVisibleRows && (
                      <li data-links-note className="px-2 py-0.5 text-[10px] text-muted-foreground">
                        Links are drawn for the first {LAYOUT.maxVisibleRows} items only; all {st.count} are listed here.
                      </li>
                    )}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
        {layout.hiddenEdges > 0 && (
          <p className="px-2 pt-1 text-[10px] text-muted-foreground">{layout.hiddenEdges} lighter links not drawn.</p>
        )}
      </div>

      <footer className="flex flex-wrap gap-x-3 gap-y-1 border-t border-border px-3.5 py-2 text-[10px] text-muted-foreground" aria-label="Legend">
        {LAMP_ORDER.filter((k) => k !== "unknown" || present.has("unknown")).map((k) => (
          <span key={k} className="inline-flex items-center gap-1" data-legend={k}>
            <Lamp kind={k} />
            {LAMP_LABEL[k]}
            {present.has(k) ? ` (${present.get(k)})` : ""}
          </span>
        ))}
        {RING_ORDER.map((r) => (
          <span key={r} className="inline-flex items-center gap-1" data-legend={r}>
            <Lamp kind="unlit" ring={r} />
            {RING_LABEL[r]}
          </span>
        ))}
      </footer>
    </section>
  );
}

// ── container ────────────────────────────────────────────────────────────────────────────────────

export interface SignalBoardProps {
  snapshot: TaskSnapshot;
  onViewItemBody?: (workItemId: string) => void;
  /** Tests inject a fetcher; the app uses R7. */
  fetchGraph?: (q: { since?: number }) => Promise<TaskGraph>;
  pollMs?: number;
  className?: string;
}

/** Mounted from the task panel: from plan proposed until the task ends. */
export function SignalBoard({ snapshot, onViewItemBody, fetchGraph, pollMs = GRAPH_POLL_MS, className }: SignalBoardProps) {
  const [state, setState] = useState<TaskGraphState>(INITIAL_GRAPH_STATE);
  const ctl = useRef<TaskGraphController | null>(null);
  const fetchRef = useRef(fetchGraph);
  fetchRef.current = fetchGraph;
  const { session_id: sessionId, task_id: taskId } = snapshot;

  useEffect(() => {
    const c = createTaskGraphController({
      fetchGraph: (q) => (fetchRef.current ? fetchRef.current(q) : getTaskGraph(sessionId, taskId, q)),
      onState: setState,
      pollMs,
    });
    ctl.current = c;
    c.refresh();
    return () => {
      c.dispose();
      ctl.current = null;
    };
  }, [sessionId, taskId, pollMs]);

  // The snapshot is refetched on the same frames R7 cares about, so a changed snapshot is the refetch cue.
  const planVersion = snapshot.plan?.version ?? 0;
  const signature = `${snapshot.state}|${snapshot.updated_at}|${snapshot.build?.status ?? ""}|${JSON.stringify(snapshot.build?.counts ?? {})}`;
  const lastPlanVersion = useRef(planVersion);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const structural = planVersion !== lastPlanVersion.current;
    lastPlanVersion.current = planVersion;
    ctl.current?.refresh({ structural });
  }, [signature, planVersion]);

  const buildActive = snapshot.build?.status === "active" && !isTerminalState(snapshot.state);
  // Poll only while the tab is visible; coming back refetches once.
  useEffect(() => {
    const hidden = () => typeof document !== "undefined" && document.hidden === true;
    ctl.current?.setActive(buildActive && !hidden());
    const onVisibility = () => {
      const visible = !hidden();
      ctl.current?.setActive(buildActive && visible);
      if (visible) ctl.current?.refresh();
    };
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility);
      ctl.current?.setActive(false);
    };
  }, [buildActive, sessionId, taskId, pollMs]);

  const bodyByItem = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of snapshot.build?.items ?? []) if (w.item_id && w.has_body) m.set(w.item_id, w.work_item_id);
    return m;
  }, [snapshot.build]);
  const openable = useMemo(() => new Set(bodyByItem.keys()), [bodyByItem]);

  if (!state.graph) return null;
  const notBuilt = !!snapshot.build && (state.graph.nodes ?? []).length > 0 && (state.graph.nodes ?? []).every((n) => n.build_status == null);
  return (
    <SignalBoardView
      graph={state.graph}
      buildActive={buildActive}
      notBuiltVersion={notBuilt ? state.graph.version : undefined}
      addedIds={state.addedIds}
      openableIds={openable}
      onOpenItem={onViewItemBody ? (itemId) => { const w = bodyByItem.get(itemId); if (w) onViewItemBody(w); } : undefined}
      className={className}
    />
  );
}
